import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

// Keep in sync with the `.product-card-collapse` transition in _category-products.scss.
export const PANEL_TRANSITION_MS = 350

interface Props {
  open: boolean
  className?: string

  // Called with the content height before the opening animation starts (in a layout effect, so the caller can
  // start its own animation in the same frame).
  onMeasure?(height: number): void

  // Called when the opening animation has finished.
  onOpened?(): void
}

// Height animation for the card's quick add-to-cart panel. MUI Collapse animates to the height measured when the
// animation starts (the loading skeleton) and snaps to the real height at the end, which made the card jump when
// the loaded form had a different height. A `grid-template-rows: 0fr -> 1fr` transition always follows the current
// content height instead. Children mount on open and unmount after the closing animation.
const PanelCollapse: React.FC<Props> = ({ open, className = '', onMeasure, onOpened, children }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(open)
  const [expanded, setExpanded] = useState(false)

  // A layout effect, so closing starts in the same frame as the caller's own closing animation (the card's image).
  useLayoutEffect(() => {
    if (open) {
      setMounted(true)
      return undefined
    }
    setExpanded(false)
    const timeout = setTimeout(() => setMounted(false), PANEL_TRANSITION_MS)
    return () => clearTimeout(timeout)
  }, [open])

  useLayoutEffect(() => {
    if (mounted && open) {
      // Lay out the collapsed (0fr) state first, otherwise the browser skips the transition.
      void ref.current?.offsetHeight
      const inner = ref.current?.firstElementChild
      if (inner && onMeasure) {
        // Layout heights (offsetHeight + margins), not scrollHeight: the content's entrance animation translates it,
        // and scrollHeight would count the translated overflow.
        const height = [...inner.children].reduce((sum, child: HTMLElement) => {
          const style = getComputedStyle(child)
          return sum + child.offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom)
        }, 0)
        onMeasure(height)
      }
      setExpanded(true)
    }
  }, [mounted, open])

  useEffect(() => {
    if (!expanded) return undefined
    const timeout = setTimeout(() => onOpened?.(), PANEL_TRANSITION_MS)
    return () => clearTimeout(timeout)
  }, [expanded])

  if (!mounted) return null
  return (
    <div ref={ref} className={`product-card-collapse ${expanded ? 'product-card-collapse--open' : ''} ${className}`}>
      <div className='product-card-collapse__inner'>{children}</div>
    </div>
  )
}

export default PanelCollapse
